import type {MediaTypeLoadingStates, SocialMediaAccountId} from '@Components/social-media/post.vo';
import type {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {didAllAccountsErrorOut, haveAllPublishingStatesSettled} from '@Libraries/social-media-wizard-accounts-library';
import {LoadingStates} from '@Utils/loading.util';

export enum SocialMediaPublishPhase {
  NOT_STARTED = 'NOT_STARTED',
  /** A request is in flight: the chrome freezes (disabled back, spinner CTA, frozen rail, previews scrim). */
  IN_FLIGHT = 'IN_FLIGHT',
  /** Every slot has settled — published, scheduled, saved or failed. The chrome shows the terminal CTA. */
  TERMINAL = 'TERMINAL',
}

export interface SocialMediaPublishPhaseInput {
  publishingAction: SocialPostPublishingAction | undefined;
  accountIdsToPublishTo: SocialMediaAccountId[];
  publishingStatusForAllAccounts: Record<SocialMediaAccountId, MediaTypeLoadingStates>;
  savingPublishingParamsState: LoadingStates;
}

const hasEverySlotFailed = (accountIdsToPublishTo: SocialMediaAccountId[], publishingStatusForAllAccounts: Record<SocialMediaAccountId, MediaTypeLoadingStates>): boolean => {
  return haveAllPublishingStatesSettled(accountIdsToPublishTo, publishingStatusForAllAccounts) && didAllAccountsErrorOut(accountIdsToPublishTo, publishingStatusForAllAccounts);
};

/** A failed save and a fresh re-arm both leave NOT_LOADED; only a failure also clears every slot. */
export const getSocialMediaPublishPhase = ({
  publishingAction,
  accountIdsToPublishTo,
  publishingStatusForAllAccounts,
  savingPublishingParamsState,
}: SocialMediaPublishPhaseInput): SocialMediaPublishPhase => {
  if (publishingAction === undefined) {
    return SocialMediaPublishPhase.NOT_STARTED;
  }

  if (savingPublishingParamsState === LoadingStates.NOT_LOADED) {
    return hasEverySlotFailed(accountIdsToPublishTo, publishingStatusForAllAccounts) ? SocialMediaPublishPhase.TERMINAL : SocialMediaPublishPhase.IN_FLIGHT;
  }

  if (savingPublishingParamsState === LoadingStates.LOADING) {
    return SocialMediaPublishPhase.IN_FLIGHT;
  }

  if (!haveAllPublishingStatesSettled(accountIdsToPublishTo, publishingStatusForAllAccounts)) {
    return SocialMediaPublishPhase.IN_FLIGHT;
  }

  return SocialMediaPublishPhase.TERMINAL;
};
